import { Cross2Icon } from "@radix-ui/react-icons";
import { Table } from "@tanstack/react-table";
import { TbStatusChange } from "react-icons/tb";
import { BiBadgeCheck } from "react-icons/bi";
import Cookies from "js-cookie";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button/button";
import { Input } from "@/components/ui/input/input";
import { DataTableViewOptions } from "./data-table-view-options";
import { DataTableFacetedFilter } from "./data-table-faceted-filter";
import { DataTableFacetedFilterReverse } from "./data-table-faceted-filter-reverse";
import { HelperButton } from "@/components/ui/helper-button/helper-button";

// import { DataTableFacetedFilter } from "./data-table-faceted-filter";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog/dialog";
import { unAssignEmployeeLeads } from "@/config/mutation/employee/lead";
import { toast } from "sonner";
import { AxiosError } from "axios";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
}

export function DataTableToolbar<TData>({
  table,
}: DataTableToolbarProps<TData>) {
  const key = Cookies.get("key") as string;
  const isFiltered = table.getState().columnFilters.length > 0;
  const queryClient = useQueryClient();

  const brokers = [
    {
      label: "Angel One",
      value: "ANGELONE",
    },
    {
      label: "Zerodha",
      value: "ZERODHA",
    },
    {
      label: "Alice Blue",
      value: "ALICEBLUE",
    },
    {
      label: "ICICI",
      value: "ICICI",
    },
    {
      label: "Upstox",
      value: "UPSTOX",
    },
  ];

  const callingStatus = [
    {
      label: "Closed (Success)",
      value: "Closed",
    },
    {
      label: "Ringing",
      value: "Ringing",
    },
    {
      label: "Switch Off",
      value: "Switch Off",
    },
    {
      label: "Busy",
      value: "Busy",
    },
    {
      label: "Not Interested",
      value: "Not Interested",
    },
    {
      label: "Call Not Connected",
      value: "Call Not Connected",
    },
    {
      label: "Invalid No",
      value: "Invalid No",
    },
    {
      label: "Account open in other broker",
      value: "Account open in other broker",
    },
    {
      label: "Call disconnect",
      value: "Call disconnect",
    },
    {
      label: "He/She doing process",
      value: "He/She doing process",
    },
    {
      label: "Document Pending",
      value: "Document Pending",
    },
    {
      label: "Mobile Number not Linked",
      value: "Mobile Number not Linked",
    },
    {
      label: "Pan/Adhar Card Not Linked",
      value: "Pan/Adhar Card Not Linked",
    },
    {
      label: "Call back",
      value: "Call back",
    },
    {
      label: "Rejection",
      value: "Rejection",
    },
    {
      label: "WAITING",
      value: "WAITING",
    },
    {
      label: "Proceed from Another Number",
      value: "Proceed from Another Number",
    },
    {
      label: "OUT OF NETWORK",
      value: "OUT OF NETWORK",
    },
    {
      label: "15 DAYS",
      value: "15 DAYS",
    },
    {
      label: "30 DAYS",
      value: "30 DAYS",
    },
    {
      label: "MONTH",
      value: "MONTH",
    },
    {
      label: "DECISION PENDING",
      value: "DECISION PENDING",
    },
    {
      label: "KYC ISSUE",
      value: "KYC ISSUE",
    },
    {
      label: "BANK ERROR",
      value: "BANK ERROR",
    },
    {
      label: "IPV",
      value: "IPV",
    },
    {
      label: "AADHAR OTP",
      value: "AADHAR OTP",
    },
    {
      label: "NAME MISMATCH",
      value: "NAME MISMATCH",
    },
    {
      label: "Repeated Leads",
      value: "Repeated Leads",
    },
  ];

  const { mutate, isPending } = useMutation({
    mutationFn: unAssignEmployeeLeads(key),
  });

  const handleAssignSelectedLeads = () => {
    const selectedRows = table.getSelectedRowModel().rows;
    const formatData = selectedRows?.map((row) => {
      const originData = row.original as {
        id: string;
        alloted: boolean;
      };

      return originData?.id;
    });

    mutate(
      {
        leads: formatData,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["leads"],
          });

          table.toggleAllPageRowsSelected(false);
          toast.success("Leads are unassigned from you.");
        },

        onError: (error) => {
          console.log(
            "ERROR WHILE UN-ASSIGNING LEADS TO CURRENT EMPLOYEE",
            error
          );
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center space-x-2">
        <Popover>
          <PopoverTrigger>
            <Button size="sm" variant="outline">
              Search with
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="flex flex-col gap-3">
              <Input
                placeholder="Filter by name..."
                value={
                  (table.getColumn("mname")?.getFilterValue() as string) ?? ""
                }
                onChange={(event) =>
                  table.getColumn("mname")?.setFilterValue(event.target.value)
                }
                className="h-8 w-[150px] lg:w-[250px]"
              />

              <Input
                placeholder="Filter by phone..."
                value={
                  (table.getColumn("phone")?.getFilterValue() as string) ?? ""
                }
                onChange={(event) =>
                  table.getColumn("phone")?.setFilterValue(event.target.value)
                }
                className="h-8 w-[150px] lg:w-[250px]"
              />
            </div>
          </PopoverContent>
        </Popover>

        {table.getColumn("broker") && (
          <DataTableFacetedFilter
            column={table.getColumn("broker")}
            title="Broker"
            options={brokers}
            Icon={BiBadgeCheck}
          />
        )}

        {table.getColumn("status") && (
          <div className="flex items-center gap-1">
            <DataTableFacetedFilterReverse
              column={table.getColumn("status")}
              title="Calling Status"
              options={callingStatus}
              Icon={TbStatusChange}
            />
            <HelperButton>
              <p>
                upon selecting calling status in this filter, leads with
                selected calling status will be shown in table. if a lead is not
                touched yet, it's calling status will be 'uploaded' by default.
              </p>
            </HelperButton>
          </div>
        )}

        {table.getSelectedRowModel().rows.length > 0 && (
          <Dialog>
            <DialogTrigger>
              <Button
                aria-label="Assign selected leads"
                size="sm"
                className="h-8 self-end"
                type="button"
                variant="destructive"
              >
                Unassign
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Are you absolutely sure?</DialogTitle>
                <DialogDescription>
                  This action will unassign only untouched leads i.e. if you
                  haven't changed a lead yet, then only it will be un-assigned,
                  otherwise it'll be ignored.
                </DialogDescription>
              </DialogHeader>

              <div className="flex flex-row items-center justify-end">
                <Button
                  disabled={isPending}
                  aria-label="Assign selected leads"
                  size="sm"
                  className="h-8 self-end"
                  type="button"
                  variant="destructive"
                  onClick={handleAssignSelectedLeads}
                >
                  Unassign
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {isFiltered && (
          <Button
            variant="ghost"
            onClick={() => table.resetColumnFilters()}
            className="h-8 px-2 lg:px-3"
          >
            Reset
            <Cross2Icon className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>

      <DataTableViewOptions table={table} />
    </div>
  );
}
